import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const today = () => new Date().toISOString().slice(0, 10);

export default function AppointmentDialog({ open, onOpenChange, doctors = [], patients = [], lockedPatient, defaultDoctor, defaultDate, onSaved }) {
  const [form, setForm] = useState({ patient_id: "", doctor_id: "", date: today(), time: "09:00", duration_min: 30, reason: "", notes: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setForm({
      patient_id: lockedPatient?.id || "", doctor_id: defaultDoctor || "",
      date: defaultDate || today(), time: "09:00", duration_min: 30, reason: "", notes: "",
    });
  }, [open, lockedPatient, defaultDoctor, defaultDate]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.patient_id) { toast.error("Select a patient"); return; }
    if (!form.doctor_id) { toast.error("Select a doctor"); return; }
    setBusy(true);
    try {
      await api.post("/clinic/appointments", { ...form, duration_min: Number(form.duration_min) || 30 });
      toast.success("Appointment booked");
      onOpenChange(false);
      onSaved?.();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="appointment-dialog">
        <DialogHeader><DialogTitle className="font-heading">Book appointment</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          {lockedPatient ? (
            <div className="text-sm bg-muted/50 rounded-md px-3 py-2">Patient: <span className="font-medium">{lockedPatient.name || `${lockedPatient.first_name} ${lockedPatient.last_name}`}</span></div>
          ) : (
            <div className="space-y-1.5"><Label>Patient</Label>
              <Select value={form.patient_id || undefined} onValueChange={(v) => set("patient_id", v)}>
                <SelectTrigger data-testid="appt-patient-select"><SelectValue placeholder="Select patient" /></SelectTrigger>
                <SelectContent>{patients.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} · {p.patient_no}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-1.5"><Label>Doctor</Label>
            <Select value={form.doctor_id || undefined} onValueChange={(v) => set("doctor_id", v)}>
              <SelectTrigger data-testid="appt-doctor-select"><SelectValue placeholder="Select doctor" /></SelectTrigger>
              <SelectContent>{doctors.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}{d.specialty ? ` — ${d.specialty}` : ""}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5"><Label>Date</Label><Input type="date" data-testid="appt-date" value={form.date} onChange={(e) => set("date", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Time</Label><Input type="time" data-testid="appt-time" value={form.time} onChange={(e) => set("time", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Mins</Label><Input type="number" data-testid="appt-duration" value={form.duration_min} onChange={(e) => set("duration_min", e.target.value)} /></div>
          </div>
          <div className="space-y-1.5"><Label>Reason</Label><Input data-testid="appt-reason" value={form.reason} onChange={(e) => set("reason", e.target.value)} placeholder="e.g. Fitting, review" /></div>
          <div className="space-y-1.5"><Label>Notes</Label><Textarea rows={2} data-testid="appt-notes" value={form.notes} onChange={(e) => set("notes", e.target.value)} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="save-appointment-button" className="bg-primary hover:bg-primary/90">{busy ? "Booking…" : "Book"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
